import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldToggle, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// Demirbaş (asset) master detay — GET /api/assets/{id} mevcut, POST/PUT/DELETE REST.
// Body = AssetUpsertRequest.
const API_BASE = '/api/assets';

const TYPE_OPTIONS = [
  { value: 'COOLER', label: 'Soğutucu' },
  { value: 'CABINET', label: 'Dolap' },
  { value: 'STAND', label: 'Stand' },
  { value: 'SHELF', label: 'Raf' },
  { value: 'DISPLAY', label: 'Vitrin' },
];
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'FAULTY', label: 'Arızalı' },
  { value: 'INACTIVE', label: 'Pasif' },
  { value: 'REMOVED', label: 'Sökülmüş' },
];
const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  FAULTY: { label: 'Arızalı', tone: 'bad' },
  INACTIVE: { label: 'Pasif', tone: 'neutral' },
  REMOVED: { label: 'Sökülmüş', tone: 'neutral' },
};
const METHOD_OPTIONS = [
  { value: 'SCAN', label: 'Barkod (SCAN)' },
  { value: 'PHOTO', label: 'Fotoğraf (PHOTO)' },
];

const EMPTY_FORM = {
  textId: '',
  customerId: '',
  customerTitle: '',
  barcode: '',
  assetType: 'COOLER',
  modelCode: '',
  modelName: '',
  brand: '',
  serialNo: '',
  isSerialTracked: false,
  status: 'ACTIVE',
  monthlyTargetOverride: '',
  countMethod: 'SCAN',
  installedAt: '',
  photoUrl: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}
function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}
function customerLookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const title = item.title || item.Title || item.name || item.Name || '';
  return title ? (code ? `${code} - ${title}` : title) : code;
}

function rowToForm(row) {
  return {
    textId: pick(row, 'textId', 'TextId') || '',
    customerId: pick(row, 'customerId', 'CustomerId') || '',
    customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
    barcode: pick(row, 'barcode', 'Barcode') || '',
    assetType: pick(row, 'assetType', 'AssetType') || 'COOLER',
    modelCode: pick(row, 'modelCode', 'ModelCode') || '',
    modelName: pick(row, 'modelName', 'ModelName') || '',
    brand: pick(row, 'brand', 'Brand') || '',
    serialNo: pick(row, 'serialNo', 'SerialNo') || '',
    isSerialTracked: !!pick(row, 'isSerialTracked', 'IsSerialTracked'),
    status: pick(row, 'status', 'Status') || 'ACTIVE',
    monthlyTargetOverride: pick(row, 'monthlyTargetOverride', 'MonthlyTargetOverride') ?? '',
    countMethod: pick(row, 'countMethod', 'CountMethod') || 'SCAN',
    installedAt: toLocalDateInput(pick(row, 'installedAt', 'InstalledAt')),
    photoUrl: pick(row, 'photoUrl', 'PhotoUrl') || '',
  };
}

export function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Demirbaş' : 'Demirbaş Detayı', 'Saha noktasındaki soğutucu / dolap / stand / raf / vitrin master kaydı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // §6 Cihaz Hedefi: model varsayılanını (AssetModel.monthlyTargetCases) modelCode ile çöz.
  const [models, setModels] = useState([]);
  const [overrideOn, setOverrideOn] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Tek kayıt: önce navigation state row ile hızlı doldur, sonra GET-by-id ile tam kayıt.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const seed = location.state?.row;
    if (seed && String(pick(seed, 'id', 'Id')) === String(id)) setForm(rowToForm(seed));
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Demirbaş alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm(rowToForm(r));
      setAudit({
        createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'),
        createdDate: pick(r, 'createdDate', 'CreatedDate'),
        updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'),
        updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate'),
      });
    } catch (err) {
      showToast('error', err.message || 'Demirbaş alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // §6 Demirbaş modellerini çek (cihaz hedefi varsayılanını çözmek için).
  useEffect(() => {
    fetch('/api/asset-models?take=500', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (!payload) return;
        const data = pick(payload, 'Data', 'data') ?? payload;
        const items = pick(data, 'items', 'Items') ?? (Array.isArray(data) ? data : []);
        setModels(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
  }, []);

  // Yüklenen kayıtta cihaza özel hedef varsa override anahtarını aç.
  useEffect(() => {
    if (!loading) {
      setOverrideOn(form.monthlyTargetOverride !== '' && form.monthlyTargetOverride !== null && form.monthlyTargetOverride !== undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // modelCode → AssetModel.monthlyTargetCases (model varsayılan cihaz hedefi).
  const modelDefaultTarget = (() => {
    if (!form.modelCode) return null;
    const m = models.find((x) => String(pick(x, 'code', 'Code')) === String(form.modelCode));
    const v = m ? pick(m, 'monthlyTargetCases', 'MonthlyTargetCases') : null;
    return (v === null || v === undefined) ? null : Number(v);
  })();

  const toggleOverride = (on) => {
    setOverrideOn(on);
    // Kapanınca cihaza özel değer temizlenir → model varsayılanı kullanılır.
    if (!on) set('monthlyTargetOverride', '');
  };

  // Klonla: /assets/new'e kopya veriyle gelindiyse formu doldur (Kaydet'e kadar kayıt oluşmaz).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    textId: (form.textId || '').trim() || null,
    customerId: (form.customerId || '').trim(),
    barcode: (form.barcode || '').trim() || null,
    assetType: form.assetType,
    modelCode: (form.modelCode || '').trim() || null,
    modelName: (form.modelName || '').trim() || null,
    brand: (form.brand || '').trim() || null,
    serialNo: (form.serialNo || '').trim() || null,
    isSerialTracked: !!form.isSerialTracked,
    status: form.status || 'ACTIVE',
    monthlyTargetOverride: form.monthlyTargetOverride === '' ? null : Number(form.monthlyTargetOverride),
    countMethod: form.countMethod || null,
    installedAt: fromLocalDateInput(form.installedAt),
    photoUrl: (form.photoUrl || '').trim() || null,
  });

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!String(form.customerId || '').trim()) { showToast('error', 'Müşteri zorunlu.'); return; }
    if (!form.assetType) { showToast('error', 'Demirbaş tipi zorunlu.'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Demirbaş eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Demirbaş oluşturuldu.');
        setTimeout(() => navigate('/assets'), 400);
        return;
      }
      showToast('success', 'Demirbaş güncellendi.');
      setTimeout(() => navigate('/assets'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => navigate('/assets/new', { state: { clone: { ...form } } });

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Demirbaş silindi.');
      setTimeout(() => navigate('/assets'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Demirbaş' : (form.barcode || form.serialNo || 'Demirbaş Detayı');
  const stMeta = STATUS_META[form.status] || { label: form.status || '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={stMeta.tone}>{stMeta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/assets')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni demirbaş aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Demirbaş Envanteri', to: '/assets' }, { label: isNew ? 'Yeni Demirbaş' : 'Demirbaş Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Asset', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Demirbaş Bilgileri" subtitle="Nokta, tip, tanım ve durum">
          <Grid cols={2}>
            <FieldCustom label="Müşteri" req invalid={tried && !String(form.customerId || '').trim()}>
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerTitle || ''}
                onChange={(val, item) => setForm((c) => ({ ...c, customerId: val || '', customerTitle: item ? customerLookupLabel(item) : '' }))}
                placeholder="Müşteri kodu veya adı ile ara…"
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="assetType" label="Demirbaş Tipi" options={TYPE_OPTIONS} req />
            <FieldText form={form} set={set} k="barcode" label="Barkod" mono placeholder="örn. DMB-2026-00184" />
            <FieldText form={form} set={set} k="textId" label="Kod (TextId)" />
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} req />
            <FieldSelect form={form} set={set} k="countMethod" label="Sayım Yöntemi" options={METHOD_OPTIONS} />
          </Grid>
        </Section>

        <Section icon="tag" title="Model & Seri" subtitle="Marka, model ve seri takibi">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="brand" label="Marka" />
            <FieldText form={form} set={set} k="modelName" label="Model Adı" />
            <FieldText form={form} set={set} k="modelCode" label="Model Kodu" />
            <FieldText form={form} set={set} k="serialNo" label="Seri No" />
            <FieldToggle form={form} set={set} k="isSerialTracked" label="Seri Takipli" hint="Sayımda seri no ile eşleştirilir" />
          </Grid>
        </Section>

        <Section icon="calendar" title="Kurulum & Cihaz Hedefi" subtitle="Kurulum tarihi, aylık cihaz hedefi ve fotoğraf">
          <Grid cols={2}>
            <FieldDate form={form} set={set} k="installedAt" label="Kurulum Tarihi" />
            <FieldCustom label="Cihaz Hedefi (Aylık, koli)">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
                  Model varsayılanı:{' '}
                  <strong style={{ color: 'var(--text-primary, #111827)' }}>
                    {modelDefaultTarget === null ? '— (model seçili değil)' : `${modelDefaultTarget} koli`}
                  </strong>
                  {form.modelCode ? '' : ' — Model Kodu girilirse çözülür'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FieldToggle
                    form={{ overrideOn }} set={() => {}} k="overrideOn"
                    label="Cihaza özel hedef" hint="Kapalıysa model varsayılanı kullanılır"
                    onChange={toggleOverride}
                  />
                </div>
                {overrideOn && (
                  <FieldText form={form} set={set} k="monthlyTargetOverride" label="Cihaza Özel Aylık Hedef (koli)" num placeholder="örn. 45" suffix="koli" />
                )}
              </div>
            </FieldCustom>
          </Grid>
          <Grid cols={2}>
            <FieldText form={form} set={set} k="photoUrl" label="Fotoğraf URL" placeholder="https://..." span />
          </Grid>
          {form.photoUrl && (
            <img
              src={form.photoUrl}
              alt="demirbaş"
              style={{ marginTop: 8, maxWidth: 200, maxHeight: 150, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal
          title="Demirbaş Sil"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.barcode || form.serialNo || title}”</strong> demirbaşını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AssetDetailPage;
